import React, { useMemo, useRef, useState } from "react"

import { OBJECT_VIEW_MODE } from "@/lib/viewModes"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import { useTestSetSimulationWrapper } from "../../hooks/useTestSetSimulationWrapper"
import { GridNavProvider, useGridNav } from "./GridNav"
import { PendingAdditionRow } from "./PendingAdditionRow"
import { TestCaseRow } from "./TestCaseRow"
import { TestCaseTableHeader } from "./TestCaseTableHeader"
import { useColumnWidths } from "./useResizableColumns"

/** Wraps the grid in its keyboard-navigation provider. */
export const TestCaseTable: React.FC = () => (
  <GridNavProvider>
    <TestCaseGrid />
  </GridNavProvider>
)

const TestCaseGrid: React.FC = () => {
  const {
    testCases,
    visibleTestCases,
    hasActiveTestCaseFilters,
    clearTestCaseFilters,
    pendingChanges,
    selectedTestCaseIds,
    visibleColumns,
    metadataViewMode,
  } = useTestSetEditorContext()

  const gridNav = useGridNav()
  const simulationWrapper = useTestSetSimulationWrapper()
  const tableRef = useRef<HTMLTableElement>(null)
  // Roving tab order: the table is the single tab stop into the grid. While a
  // cell is focused the container leaves the tab order, so Tab / Shift+Tab exit
  // the grid (rather than bouncing back to the container). It is restored to a
  // tab stop once focus leaves the grid entirely.
  const [gridTabIndex, setGridTabIndex] = useState(0)

  const handleTableFocus = (e: React.FocusEvent<HTMLTableElement>) => {
    if (e.target === e.currentTarget) {
      // Tab landed on the container — delegate into the active (or first) cell.
      if (tableRef.current) {
        gridNav.focusActiveCell(tableRef.current)
      }
    } else {
      // A cell took focus — drop the container from the tab order.
      setGridTabIndex(-1)
    }
  }

  const handleTableBlur = (e: React.FocusEvent<HTMLTableElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setGridTabIndex(0)
    }
  }

  const hasAddColumn = metadataViewMode !== OBJECT_VIEW_MODE.JSON

  const extraFixedColumns = useMemo(() => {
    const cols = new Set<"simulationMetadata" | "actions">()
    if (simulationWrapper) {
      cols.add("simulationMetadata")
    }
    return cols
  }, [simulationWrapper])

  const columnWidths = useColumnWidths(
    visibleColumns,
    hasAddColumn,
    extraFixedColumns,
  )

  // Original position of each test case in the full list, so the displayed row
  // number stays stable even when the visible list is narrowed by filters.
  const originalIndexById = useMemo(() => {
    const map = new Map<string, number>()
    testCases.forEach((testCase, index) => map.set(testCase.id, index))
    return map
  }, [testCases])

  return (
    <div className="table-container w-full">
      <style>{`
        .table-container::-webkit-scrollbar {
          display: none;
        }
        .test-case-scroll-container::-webkit-scrollbar {
          display: none;
        }
        .test-case-table td,
        .test-case-table th {
          border-right: 1px solid var(--coval-border);
        }
        .test-case-table td:last-child,
        .test-case-table th:last-child {
          border-right: none;
        }
        /* Focus ring on every focused state of the grid: keyboard roving, click,
           and while editing (focus moves to a child, so :focus-within). It
           replaces the app-wide focus outline on the cell. */
        .test-case-table td[data-grid-cell]:focus,
        .test-case-table td[data-grid-cell]:focus-within {
          box-shadow: inset 0 0 0 2px var(--coval-contrast);
          outline: none !important;
        }
        .test-case-table:focus-visible {
          outline: 2px solid var(--coval-contrast);
          outline-offset: -2px;
        }
        .test-case-table thead th:focus-visible {
          outline: none !important;
          box-shadow:
            inset 0 0 0 2px var(--coval-contrast),
            inset 0 -1px 0 var(--coval-border);
        }
        .test-case-table thead th {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: inset 0 -1px 0 var(--coval-border);
          transform: none !important;
          scale: none !important;
          background-color: var(--surface-default);
          transition: background-color 150ms;
          outline: none !important;
        }
        .test-case-table thead th:hover {
          background-color: var(--surface-0);
        }
        @keyframes row-highlight-flash {
          0%   { box-shadow: inset 0 0 0 9999px color-mix(in srgb, var(--coval-text) 20%, transparent); }
          100% { box-shadow: inset 0 0 0 9999px transparent; }
        }
        .test-case-table tr.row-highlight-flash {
          animation: row-highlight-flash 1.2s ease-out forwards;
        }
      `}</style>
      <table
        ref={tableRef}
        role="grid"
        aria-label="Test cases"
        tabIndex={gridTabIndex}
        onFocus={handleTableFocus}
        onBlur={handleTableBlur}
        className="test-case-table w-full outline-none"
        style={{ tableLayout: "fixed" }}
      >
        <TestCaseTableHeader columnWidths={columnWidths} />
        <tbody>
          {visibleTestCases.map((testCase, index) => (
            <TestCaseRow
              key={testCase.id}
              testCase={testCase}
              index={index}
              displayIndex={originalIndexById.get(testCase.id) ?? index}
              columnWidths={columnWidths}
              isMultiSelected={selectedTestCaseIds.has(testCase.id)}
            />
          ))}

          {/* No test cases match the active search/type filters */}
          {hasActiveTestCaseFilters &&
            visibleTestCases.length === 0 &&
            testCases.length > 0 && (
              <tr>
                <td colSpan={100} className="px-6 py-12 text-center">
                  <p className="text-sm text-coval-text-muted">
                    No test cases match your search or filters.
                  </p>
                  <button
                    type="button"
                    onClick={clearTestCaseFilters}
                    className="mt-2 text-sm text-coval-contrast underline"
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            )}

          {/* Pending additions */}
          {pendingChanges?.type === "ADD" &&
            pendingChanges.data.map((testCase, index) => (
              <PendingAdditionRow
                key={`pending-${testCase.id}`}
                testCase={testCase}
                index={visibleTestCases.length + index}
                displayIndex={testCases.length + index}
                isFirst={index === 0}
                isMultiSelected={selectedTestCaseIds.has(testCase.id)}
                columnWidths={columnWidths}
              />
            ))}
        </tbody>
      </table>
    </div>
  )
}
